import { Box, HStack, Icon } from "@chakra-ui/react"
import {
  FaBolt,
  FaCircleCheck,
  FaCircleExclamation,
  FaCirclePause,
  FaCirclePlay,
  FaCircleQuestion,
  FaFlask,
  FaWrench,
} from "react-icons/fa6"
import type { IconType } from "react-icons"
import { AssetStatus, TenantStatusCopy } from "@hydrologiq/gql-experience/graphql"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { statusToText } from "@hydrologiq/shared/asset/attributes/status"

/**
 * Icon + colour per electrolyser status, shared by the per-unit card and the stack card so a
 * status looks the same wherever it appears.
 *
 * Deliberately SEMANTIC (green = running, amber = idle, red = fault) rather than reusing
 * StatusBadge's palette, which gives every status its own decorative hue (error is pink there) —
 * fine for a table cell you read carefully, not for a figure you glance at. The icon carries the
 * same signal as the colour so it still reads when colour vision or contrast is a problem.
 *
 * Only the statuses fixed plant can hold are mapped; the logistics ones (Inbound, Reserved, …)
 * apply to movable assets and fall through to the neutral default.
 */
export const STATUS_VISUALS: Partial<Record<AssetStatus, { icon: IconType; color: string }>> = {
  [AssetStatus.Active]: { icon: FaBolt, color: "#50c878" },
  [AssetStatus.Filling]: { icon: FaBolt, color: "#50c878" },
  [AssetStatus.Available]: { icon: FaCircleCheck, color: "#50c878" },
  [AssetStatus.Startup]: { icon: FaCirclePlay, color: "#39aed2" },
  [AssetStatus.Standby]: { icon: FaCirclePause, color: "#f0986f" },
  [AssetStatus.Servicing]: { icon: FaWrench, color: "#f0986f" },
  [AssetStatus.Testing]: { icon: FaFlask, color: "#39aed2" },
  [AssetStatus.Error]: { icon: FaCircleExclamation, color: "#d2042d" },
}

export const DEFAULT_VISUAL = { icon: FaCircleQuestion, color: "#a9a9a9" }

export function visualFor(status: AssetStatus | null) {
  return (status && STATUS_VISUALS[status]) || DEFAULT_VISUAL
}

/**
 * The figure value line for an electrolyser status: label then icon.
 *
 * Falling back to the enum member rather than a literal keeps tenant status copy applied, so a
 * tenant that renames "Unknown" still sees their wording.
 */
export function ElectrolyserStatusValue({
  status,
  statusCopy,
  trailing,
}: {
  status: AssetStatus | null
  statusCopy: TenantStatusCopy[]
  /** Small supporting detail to the right of the icon, e.g. a stack's combined flow. */
  trailing?: React.ReactNode
}) {
  const { icon, color } = visualFor(status)
  return (
    // Never wraps: the row has to keep a fixed height so cards in a strip stay level. If the status
    // and its detail can't both fit, the status truncates rather than the detail dropping to a new
    // line and making this card taller than its neighbours.
    <HStack gap={2} alignItems="center" width="100%" flexWrap="nowrap" minWidth={0}>
      <FigureContent.Value truncate>
        {statusToText(status ?? AssetStatus.Unknown, statusCopy)}
      </FigureContent.Value>
      {/* em resolves against the inherited base size, not the value's 26px — 1.15em ≈ 18px.
          `top` drops it ~2px off the line-box centre onto the x-height centre, so it reads as
          aligned with the lowercase body of the label rather than floating above it.

          With nothing trailing it, the auto margin pushes it onto the card's content edge, matching
          Capacity to Fill so the two cards line their glyphs up side by side rather than each
          sitting wherever its status text happened to end. Where there IS trailing detail the icon
          keeps its place beside the status, which it belongs to. */}
      <Icon
        as={icon}
        color={color}
        boxSize="1.15em"
        flexShrink={0}
        ms={trailing ? undefined : "auto"}
        position="relative"
        top="2px"
        aria-hidden
      />
      {/* Extra inset on top of the row gap: the icon belongs to the status beside it, so the
          supporting values need clearly more separation than the status-to-icon spacing. */}
      {trailing && (
        <Box ms={3} flexShrink={0}>
          {trailing}
        </Box>
      )}
    </HStack>
  )
}
